import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  change: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'yellow' | 'red';
  onClick?: () => void;
}

const colorClasses = {
  blue: 'bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-300 border-brand-200/60 dark:border-brand-800/60',
  green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-800/60',
  yellow: 'bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200/60 dark:border-amber-800/60',
  red: 'bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200/60 dark:border-rose-800/60',
};

export const StatCard: React.FC<StatCardProps> = ({ title, value, change, icon, color, onClick }) => {
  const TrendIcon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
  const trendColor = change > 0 ? 'text-emerald-600 dark:text-emerald-400' : change < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500';
  const trendBg = change > 0 ? 'bg-emerald-50 dark:bg-emerald-950/50' : change < 0 ? 'bg-rose-50 dark:bg-rose-950/50' : 'bg-slate-100 dark:bg-slate-800';

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'card w-full text-left transition-all duration-200 group relative overflow-hidden',
        onClick ? 'cursor-pointer hover:border-brand-300 dark:hover:border-slate-700 hover:shadow-card-hover active:scale-[0.99]' : 'cursor-default',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 truncate">
            {title}
          </p>
          <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-slate-100 mt-1.5 tracking-tight">
            {value}
          </p>
          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${trendBg} ${trendColor}`}>
              <TrendIcon size={13} />
              <span>%{Math.abs(change)}</span>
            </span>
            <span className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">Bu ay</span>
          </div>
        </div>
        <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center border shadow-sm flex-shrink-0 transition-transform group-hover:scale-105 ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </button>
  );
};
